import addDays from 'date-fns/addDays';
import differenceInDays from 'date-fns/differenceInDays';
import isToday from 'date-fns/isToday';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import range from 'lodash/range';

import crossSearch from '@ott/utility-cross-search';

import {
  MAX_ADULTS,
  MAX_CHILDREN,
  DEFAULT_ADULTS,
  DEFAULT_CHILDREN,
  DEFAULT_CHILDREN_AGE,
  DATE_FORMAT,
  HOTELS_LAST_SEARCH_KEY,
  HOTELS_SEARCH_HISTORY_KEY,
} from '../constants';
import {
  Query,
  Destination,
  Room,
  SearchParams,
  HotelsConfig,
  LastSearchRecord,
  SearchHistoryRecord,
} from '../types';
import convertRoomsForQuery from './convertRoomsForQuery';
import getGuestsCount from './getGuestsCount';
import { getMaxDaysIntervalByConfig } from './getMaxDaysIntervalByConfig';
import getMultiroomParamsByConfig from './getMultiroomParamsByConfig';

import sels from 'sels';

const timeRegex = /\d\d:\d\d/;

const getNormalizedTimezoneGMT = (value: unknown): number | undefined => {
  if (!value && value !== 0) {
    return undefined;
  }

  const result = Number(value);

  return Number.isNaN(result) ? undefined : result;
};

const getDestinationFromQuery = (query: Query): Destination => {
  const {
    id,

    type = 'geo',

    country_code: countryCode,
    timezone_gmt: timezoneGMT,

    to = '',
  } = query;

  return {
    id,

    type,

    // Указываем страну как пробел, так как не можем получить её название здесь,
    // а если передать falsey значение, то в Input выведется дубль города.
    // Например: МинскМинск
    country: ' ',
    country_code: countryCode,
    timezone_gmt: getNormalizedTimezoneGMT(timezoneGMT),

    name: decodeURIComponent(to),
  };
};

const getDestinationFromHistory = (query: Query): Destination | null => {
  try {
    const destinationsFromLocalStorage = sels.get(HOTELS_SEARCH_HISTORY_KEY) || '[]';
    const destinations: SearchHistoryRecord[] = JSON.parse(destinationsFromLocalStorage);

    const destination = destinations.find((item) => Number(item.params.id) === Number(query.id));

    if (!destination) {
      return null;
    }

    const {
      params: {
        id,

        type,

        country_name: countrySlug,
        country_code: countryCode,
        timezone_gmt: timezoneGMT,

        to,

        city_name: citySlug,
      },
    } = destination;

    return {
      id,

      type,

      // Указываем страну как пробел, так как не можем получить её название здесь,
      // а если передать falsey значение, то в Input выведется дубль города.
      // Например: МинскМинск
      country: ' ',
      country_code: countryCode,
      timezone_gmt: getNormalizedTimezoneGMT(timezoneGMT),

      name: decodeURIComponent(to),

      country_slug: countrySlug,
      city_slug: citySlug,
    };
  } catch (error) {
    console.error('[hotels-search-form] Search history parse error', error);

    return null;
  }
};

const getDestinationFromLastSearch = (): Destination | null => {
  try {
    const lastSearchByLocalStorage = sels.get(HOTELS_LAST_SEARCH_KEY) || '{}';
    const lastSearch: LastSearchRecord = JSON.parse(lastSearchByLocalStorage);

    return lastSearch.to?.id ? lastSearch.to : null;
  } catch (error) {
    console.error('[hotels-search-form] Last search parse error', error);

    return null;
  }
};

export const getCurrentDestination = (query: Query): Destination | null =>
  query.id
    ? getDestinationFromHistory(query) || getDestinationFromQuery(query)
    : getDestinationFromLastSearch();

export const getCurrentDates = (
  query: Query,
  maxDaysInterval: number
): { startDate: Date; endDate: Date } => {
  const nowDate = new Date();

  let { date_start: startDate, date_end: endDate } = query;

  startDate = parse(startDate, DATE_FORMAT, nowDate);
  endDate = parse(endDate, DATE_FORMAT, nowDate);

  if (!isValid(startDate) || !isValid(endDate)) {
    const lastSearchByLocalStorage = sels.get(HOTELS_LAST_SEARCH_KEY);

    if (lastSearchByLocalStorage) {
      try {
        const lastSearch: LastSearchRecord = JSON.parse(lastSearchByLocalStorage);

        startDate = parseISO(lastSearch.dates.startDate || '');
        endDate = parseISO(lastSearch.dates.endDate || '');
      } catch (error) {
        console.error('[hotels-search-form] Last search parse error', error);
      }
    }
  }

  if (!isValid(startDate)) {
    startDate = nowDate;
  }

  if (!isValid(endDate)) {
    endDate = addDays(startDate, 1);
  }

  if (differenceInDays(nowDate, startDate) > 0) {
    startDate = nowDate;
  }

  const diffFromEndDate = differenceInDays(endDate, startDate);

  if (diffFromEndDate > maxDaysInterval) {
    endDate = addDays(startDate, maxDaysInterval);
  }

  if (diffFromEndDate < 1) {
    endDate = addDays(startDate, 1);
  }

  return {
    startDate,
    endDate,
  };
};

export const getRoomsFromGuests = (query: Query): Room[] => {
  const {
    adults: adultsFromQuery,
    children: childrenFromQuery,
    childrenAges: childrenAgesFromQuery,
  } = query;

  const children = Math.min(childrenFromQuery, MAX_CHILDREN) || DEFAULT_CHILDREN;
  let childrenAges: number[] = [];

  if (childrenAgesFromQuery) {
    if (Array.isArray(childrenAgesFromQuery)) {
      childrenAges = childrenAgesFromQuery.map(Number);
    } else if (typeof childrenAgesFromQuery === 'string') {
      childrenAges = childrenAgesFromQuery.split(',').map(Number);
    }
  }

  if (childrenAges.length) {
    if (childrenAges.length < children) {
      const defaultAges = range(children - childrenAges.length).map(() => DEFAULT_CHILDREN_AGE);
      childrenAges = [...childrenAges, ...defaultAges];
    } else {
      childrenAges = childrenAges.slice(0, children);
    }
  } else {
    childrenAges = range(children).map(() => DEFAULT_CHILDREN_AGE);
  }

  return [
    {
      adults: Math.min(adultsFromQuery, MAX_ADULTS) || DEFAULT_ADULTS,
      childrenAges,
    },
  ];
};

export const parseRooms = (query: Query) => {
  const { rooms = [] } = query;

  if (!rooms.length) {
    return getRoomsFromGuests(query);
  }

  return rooms.map((room: string) => {
    const [adults, ...childrenAges] = room.split(',').map((guest) => Number(guest));

    return {
      adults: Math.min(adults, MAX_ADULTS) || DEFAULT_ADULTS,
      childrenAges: childrenAges.length
        ? childrenAges.slice(0, MAX_CHILDREN)
        : range(DEFAULT_CHILDREN).map(() => DEFAULT_CHILDREN_AGE),
    };
  });
};

export const getCurrentRooms = (
  query: Query,
  hasMultiroom: boolean,
  maxGuestsCount: number
): { initialRooms: Room[]; rooms: Room[]; canCombineToOneRoom: boolean } => {
  const rooms = parseRooms(query);

  if (hasMultiroom) {
    const availableGuestsCount = maxGuestsCount - getGuestsCount(rooms, false);

    if (availableGuestsCount < 0) {
      return {
        rooms: [
          {
            adults: DEFAULT_ADULTS,
            childrenAges: [],
          },
        ],
        initialRooms: rooms,
        canCombineToOneRoom: false,
      };
    }

    return {
      rooms,
      initialRooms: rooms,
      canCombineToOneRoom: false,
    };
  }

  const guestsInOneRoom: Room = rooms.reduce(
    (acc: Room, room: Room) => ({
      adults: acc.adults + room.adults,
      childrenAges: [...acc.childrenAges, ...room.childrenAges],
    }),
    { adults: 0, childrenAges: [] }
  );

  const isCombined = maxGuestsCount - getGuestsCount([guestsInOneRoom], false) >= 0;

  if (isCombined) {
    return {
      rooms: [guestsInOneRoom],
      initialRooms: rooms,
      canCombineToOneRoom: true,
    };
  }

  return {
    rooms: [
      {
        adults: DEFAULT_ADULTS,
        childrenAges: [],
      },
    ],
    initialRooms: rooms,
    canCombineToOneRoom: false,
  };
};

export const getCurrentCheckIOTimes = (query: Query) => {
  const { e_checkin: earlyCheckin, l_checkout: lateCheckout, date_start: startDate } = query;
  const nowDate = new Date();
  const parsedStartDate = parse(startDate, DATE_FORMAT, nowDate);

  const canSpecifyCheckinTime = isValid(parsedStartDate) && !isToday(parsedStartDate);

  return {
    lateCheckout: timeRegex.test(lateCheckout) ? lateCheckout : null,
    earlyCheckin: timeRegex.test(earlyCheckin) && canSpecifyCheckinTime ? earlyCheckin : null,
  };
};

export type GetterOptions = {
  canUseCrossSearch?: boolean;
  hotelsConfig?: HotelsConfig;
  countryCode?: string;
};

export const getCurrentSearchParams = (query: Query, options: GetterOptions): SearchParams => {
  const { canUseCrossSearch = false, hotelsConfig, countryCode } = options;
  const currentDestination = getCurrentDestination(query);

  const { maxGuestsCount, hasMultiroom } = getMultiroomParamsByConfig(hotelsConfig);
  const maxDaysIntervalByConfig = getMaxDaysIntervalByConfig({
    hotelsConfig,
    countryCode: countryCode || currentDestination?.country_code,
  });

  const currentSearchParams = {
    to: currentDestination,
    calendarOpened: Boolean(query.calendar_opened),
    maxDays: maxDaysIntervalByConfig,
    ...getCurrentRooms(query, hasMultiroom, maxGuestsCount),
    ...getCurrentDates(query, maxDaysIntervalByConfig),
    ...getCurrentCheckIOTimes(query),
  };

  if (canUseCrossSearch) {
    const crossSearchData = crossSearch.getHotel();

    if (crossSearchData) {
      return {
        ...currentSearchParams,
        ...crossSearchData,
      };
    }
  }

  return currentSearchParams;
};

export const getRoomsAndDatesForQuery = (query: Query, options: GetterOptions) => {
  const { startDate, endDate } = getCurrentSearchParams(query, options);

  let { rooms = [] } = query;

  if (!rooms.length) {
    rooms = convertRoomsForQuery(getRoomsFromGuests(query));
  }

  return {
    startDate,
    endDate,
    rooms,
  };
};
